import { CaretLeft, CaretRight } from '@phosphor-icons/react'
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import TribeOrgContainer from '../../components/TribeOrg/TribeOrgContainer'
import TribeOrgHeaderContainer from '../../components/TribeOrg/TribeOrgHeaderContainer'
import { useLazyGetAllTribesOrgQuery } from '../../redux/tribe.endpoints'
import { User } from '../../types/User'
import { Helmet } from 'react-helmet'
import { useLazyGetAllRolesQuery } from '../../redux/role.endpoints'
import { AllGuildTypes, Role } from '../../types/Roles'
import { SelectOption } from '../../types/SelectOption'
import {
  useLazyGetDatesSnapshotsQuery,
  useLazyGetSnapshotsFilteredByDateQuery,
} from '../../redux/team.endpoints'
import moment from 'moment'
import {
  areDatesOnSameDay,
  getMondayDateFromWeek,
  getWeekOfYear,
} from '../../utils/general'
import usePersistentState from '../../hooks/usePersistentState'
import { getYearOptions } from '../../utils/dropdownOptionHelper'
import Loader from '../../components/Loader/Loader'

interface Props {
  profile: User
  companyId: number
  readOnly?: boolean
}

const getTeamTribe = (team: { tribeId?: number } | null | undefined) => {
  return team?.tribeId ?? 0
}

const formatDate = (date: string | Date) => {
  return moment.utc(date).format('MMM D, YYYY')
}

const getAvailableWeeksForYear = (
  snapshotDates: SelectOption[],
  yearValue: string,
) => {
  return snapshotDates
    .filter(snapshotDate => {
      const date = moment(snapshotDate.value).utc()
      if (!date.isValid()) return false
      return date.isoWeekYear() === parseInt(yearValue)
    })
    .map(snap => `${moment(snap.value).utc().isoWeek()}`)
}

const TribeOrgPage = ({ companyId, readOnly = false }: Props) => {
  const today = new Date()
  const currentYear = today.getFullYear().toString()
  const [tribesOrg, setTribesOrg] = useState<any[]>([])
  const [currentTribe, setCurrentTribe] = useState<any[]>([])
  const [selectedYear, setSelectedYear] = useState<SelectOption>({
    value: currentYear,
    label: currentYear,
  })
  const [selectedWeek, setSelectedWeek] = useState<SelectOption>({
    value: '',
    label: '',
  })
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [selectedSnapshot, setSelectedSnapshot] = useState<SelectOption>({
    value: '',
    label: '',
  })
  const [snapshotDates, setSnapshotDates] = useState<SelectOption[]>([])
  const [getTribesOrg, getTribesOrgResponse] = useLazyGetAllTribesOrgQuery()
  const { isLoading: isLoadingTribesOrg } = getTribesOrgResponse
  const [getDatesSnapshots, getDatesSnapshotsResponse] =
    useLazyGetDatesSnapshotsQuery()
  const [getSnapshotsFilteredByDate, getSnapshotsFilteredByDateResponse] =
    useLazyGetSnapshotsFilteredByDateQuery()
  const [isLoading, setIsLoading] = useState(false)

  const [currentIndex, setCurrentIndex] = usePersistentState(
    'TribeOrg_lastViewedIndex',
    0,
  )
  const [getAllRoles, getAllRolesResponse] = useLazyGetAllRolesQuery()
  const [guilds, setGuilds] = useState<Array<Role>>([])
  const slideRefs = useRef<(HTMLDivElement | null)[]>([])
  const [stageMinHeight, setStageMinHeight] = useState<number | undefined>()

  const updateStageHeight = useCallback(() => {
    const heights = slideRefs.current.map(el => el?.offsetHeight ?? 0)
    const maxHeight = Math.max(0, ...heights)
    if (maxHeight > 0) {
      setStageMinHeight(maxHeight)
    }
  }, [])

  useLayoutEffect(() => {
    slideRefs.current = slideRefs.current.slice(0, tribesOrg.length)
    updateStageHeight()
  }, [tribesOrg, updateStageHeight])

  useEffect(() => {
    const observers = slideRefs.current
      .map(el => {
        if (!el) return null
        const observer = new ResizeObserver(updateStageHeight)
        observer.observe(el)
        return observer
      })
      .filter(Boolean) as ResizeObserver[]

    return () => {
      observers.forEach(observer => observer.disconnect())
    }
  }, [tribesOrg, updateStageHeight])

  const isLoadingSnapshot =
    isLoading || getSnapshotsFilteredByDateResponse.isFetching
  const isBusy = isLoadingTribesOrg || isLoadingSnapshot

  const handlePrevTribe = () => {
    if (isBusy || tribesOrg.length === 0) return
    setCurrentIndex((currentIndex - 1 + tribesOrg.length) % tribesOrg.length)
  }

  const handleNextTribe = () => {
    if (isBusy || tribesOrg.length === 0) return
    setCurrentIndex((currentIndex + 1) % tribesOrg.length)
  }

  const applyCurrentState = useCallback(() => {
    setSelectedYear({ value: currentYear, label: currentYear })
    setSelectedWeek({ label: '', value: '' })
    setSelectedSnapshot({ label: '', value: '' })
    setStartDate('')
    setEndDate('')
    setTribesOrg(currentTribe)
    setIsLoading(false)
  }, [currentYear, currentTribe])

  const handleYearChange = useCallback(
    (option: SelectOption) => {
      setSelectedYear(option)
      if (!selectedWeek.value) {
        const currentWeek = moment().isoWeek()
        setSelectedWeek({
          value: `${currentWeek}`,
          label: `Week ${currentWeek}`,
        })
      }
    },
    [selectedWeek.value],
  )

  const getGuildNameWithType = (guildType: string) => {
    if (guilds && guilds.length > 0) {
      return guilds.find(guild => guild.type === guildType)?.role ?? ''
    } else {
      return ''
    }
  }

  const getGuildIdWithType = (guildType: string) => {
    if (guilds && guilds.length > 0) {
      return guilds.find(guild => guild.type === guildType)?.id ?? -1
    } else {
      return -1
    }
  }

  const buildSnapshot = () => {
    if (
      getSnapshotsFilteredByDateResponse &&
      getSnapshotsFilteredByDateResponse.data &&
      snapshotDates.length > 0 &&
      selectedSnapshot.value
    ) {
      const snapshotDate = new Date(selectedSnapshot.value)

      const snapshots = getSnapshotsFilteredByDateResponse.data

      const groupByTribe = [...snapshots]
        .sort((a, b) => (getTeamTribe(a.team) > getTeamTribe(b.team) ? 1 : -1))
        .filter(snap => {
          return areDatesOnSameDay(snap.snapshotDate, snapshotDate)
        })
        .reduce((group, snap) => {
          const { tribe } = snap.team
          let none = 0
          if (tribe) none = tribe.id
          group[none] = group[none] ?? []
          group[none].push(snap)
          return group
        }, {})

      const snapshotTribes: any[] = []

      Object.keys(groupByTribe).forEach(element => {
        const tribe = groupByTribe[element][0].team.tribe
        snapshotTribes.push({
          name: tribe.name,
          updated_at: tribe.updated_at,
          teams: groupByTribe[element]
            .sort((a, b) => (a.team.id > b.team.id ? 1 : -1))
            .map(snap => {
              return {
                ...snap.team,
                engagements: snap.engagements,
                teamAllocations: snap.users.map(user => {
                  const userGuild = snap.userGuilds.find(
                    item => item.id === user.id,
                  )

                  const snapUser = { ...user, guildId: userGuild.guild }
                  return {
                    allocation: 0,
                    role: userGuild ? userGuild.role : '',
                    teamId: snap.team.id,
                    userId: user.id,
                    user: snapUser,
                  }
                }),
              }
            }),
        })
      })

      return snapshotTribes
    }
    return currentTribe
  }

  const getSnapshotsByDate = async (
    startDate: string,
    endDate: string,
    mondayDate: string,
  ) => {
    await getSnapshotsFilteredByDate({ companyId, startDate, endDate }).then(
      resp => {
        if (resp.status === 'fulfilled') {
          const selectedSnapshot = {
            value: mondayDate,
            label: `${formatDate(mondayDate)} - ${formatDate(
              moment(mondayDate).add(4, 'days').toDate(),
            )}`,
          }
          setSelectedSnapshot(selectedSnapshot)
        } else {
          setIsLoading(false)
        }
      },
    )
  }

  useEffect(() => {
    getAllRoles()
  }, [])

  useEffect(() => {
    if (getAllRolesResponse && getAllRolesResponse.data) {
      setGuilds(getAllRolesResponse.data)
    }
  }, [getAllRolesResponse])

  useEffect(() => {
    if (getDatesSnapshotsResponse && getDatesSnapshotsResponse.data) {
      const dates = getDatesSnapshotsResponse.data.map(snapshotDates =>
        moment.utc(snapshotDates.snapshotDate).toISOString(),
      )
      const setDates = new Set([...dates])

      const snapshotOptions: SelectOption[] = Array.from(setDates)
        .sort(function (a, b) {
          return new Date(b).getTime() - new Date(a).getTime()
        })
        .map(snapshotDates => {
          return {
            value: snapshotDates,
            label: `${formatDate(snapshotDates)} - ${formatDate(
              moment(snapshotDates).add(4, 'days').toDate(),
            )}`,
          }
        })
      setSnapshotDates(snapshotOptions)
    }
  }, [getDatesSnapshotsResponse, selectedYear])

  useEffect(() => {
    buildSnapshot()
  }, [snapshotDates])

  useEffect(() => {
    getTribesOrg({
      companyId: companyId,
      pageSize: 0,
      pageNumber: 0,
      sortBy: 'id',
      searchQuery: '',
      sortType: 'DESC',
    })
    getDatesSnapshots(companyId)
  }, [])

  useEffect(() => {
    if (getTribesOrgResponse && getTribesOrgResponse.data) {
      const sortedTribes = [...getTribesOrgResponse.data].sort((a, b) =>
        getTeamTribe(a.team) > getTeamTribe(b.team) ? 1 : -1,
      )
      setTribesOrg(sortedTribes)
      setCurrentTribe(sortedTribes)
    }
  }, [getTribesOrgResponse])

  useEffect(() => {
    if (selectedSnapshot.value !== '') {
      const weekOfYear = getWeekOfYear(selectedSnapshot.value)
      if (weekOfYear.toString() !== selectedWeek.value)
        setSelectedWeek({ value: `${weekOfYear}`, label: `Week ${weekOfYear}` })
      const tribe = buildSnapshot()
      setTribesOrg([...tribe])
      setIsLoading(false)
    } else {
      setTribesOrg([...currentTribe])
      setIsLoading(false)
    }
  }, [selectedSnapshot])

  const guildsInfo = [
    {
      name: getGuildNameWithType(AllGuildTypes.Developer),
      color: '#e2d9f3',
      guildId: getGuildIdWithType(AllGuildTypes.Developer),
    },
    {
      name: getGuildNameWithType(AllGuildTypes.QA),
      color: '#FFE5D0',
      guildId: getGuildIdWithType(AllGuildTypes.QA),
    },
    {
      name: getGuildNameWithType(AllGuildTypes.Design),
      color: '#D2F4EA',
      guildId: getGuildIdWithType(AllGuildTypes.Design),
    },
    {
      name: getGuildNameWithType(AllGuildTypes.DevOps),
      color: '#E7F1FF',
      guildId: getGuildIdWithType(AllGuildTypes.DevOps),
    },
  ]

  useEffect(() => {
    if (selectedWeek.value) {
      if (selectedYear.value === currentYear) {
        const availableWeeks = getAvailableWeeksForYear(
          snapshotDates,
          selectedYear.value,
        )
        if (!availableWeeks.includes(selectedWeek.value)) {
          applyCurrentState()
          return
        }
      }

      const mondayDate = getMondayDateFromWeek(
        selectedWeek.value,
        selectedYear.value,
      )
      setIsLoading(true)
      setStartDate(moment(mondayDate).format('YYYY-MM-DD'))
      setEndDate(moment(mondayDate).add(4, 'days').format('YYYY-MM-DD'))
      getSnapshotsByDate(
        moment(mondayDate).format('YYYY-MM-DD'),
        moment(mondayDate).add(4, 'days').format('YYYY-MM-DD'),
        mondayDate,
      )
    } else {
      setSelectedSnapshot({ value: '', label: '' })
      setStartDate('')
      setEndDate('')
    }
  }, [
    selectedWeek,
    selectedYear.value,
    snapshotDates,
    currentYear,
    applyCurrentState,
  ])

  useEffect(() => {
    if (currentIndex >= tribesOrg.length && tribesOrg.length > 0) {
      setCurrentIndex(0)
    }
  }, [tribesOrg.length, currentIndex])

  const yearOptions = getYearOptions()
  const safeIndex =
    tribesOrg.length > 0 ? Math.min(currentIndex, tribesOrg.length - 1) : 0

  return (
    <div className="page-content tribe-org">
      <Helmet title="Time Off - Tribe Org" />
      <h1 className="tribe-org__title">Tribe Org</h1>

      <TribeOrgHeaderContainer
        guildsInfo={guildsInfo}
        guilds={guilds}
        tribesOrg={tribesOrg}
        startDate={startDate}
        endDate={endDate}
        selectedWeek={selectedWeek}
        selectedYear={selectedYear}
        selectedSnapshot={selectedSnapshot}
        setSelectedWeek={setSelectedWeek}
        setSelectedYear={setSelectedYear}
        onYearChange={handleYearChange}
        setSelectedSnapshot={setSelectedSnapshot}
        snapshotDates={snapshotDates}
        yearOptions={yearOptions}
        setCurrent={applyCurrentState}
        isBusy={isBusy}
      />

      {isLoadingTribesOrg ? <Loader /> : tribesOrg.length > 0 ? (
        <div
          className={`tribe-org__carousel ${
            isLoadingSnapshot ? 'tribe-org__carousel--loading' : ''
          }`}
          aria-busy={isLoadingSnapshot}
        >
          {isLoadingSnapshot && (
            <div className="tribe-org__carousel-overlay">
              <Loader fitContainer />
            </div>
          )}
          <div
            className="tribe-org__carousel-stage"
            style={
              stageMinHeight ? { minHeight: `${stageMinHeight}px` } : undefined
            }
          >
            {tribesOrg.length > 1 && (
              <>
                <button
                  type="button"
                  className="tribe-org__carousel-nav tribe-org__carousel-nav--prev"
                  onClick={handlePrevTribe}
                  disabled={isBusy}
                  aria-label="Previous tribe"
                >
                  <CaretLeft size={28} weight="bold" />
                </button>
                <button
                  type="button"
                  className="tribe-org__carousel-nav tribe-org__carousel-nav--next"
                  onClick={handleNextTribe}
                  disabled={isBusy}
                  aria-label="Next tribe"
                >
                  <CaretRight size={28} weight="bold" />
                </button>
              </>
            )}

            <div className="tribe-org__carousel-viewport">
              <div
                className="tribe-org__carousel-track"
                style={{ transform: `translateX(-${safeIndex * 100}%)` }}
              >
                {tribesOrg.map((tribe, index) => (
                  <div
                    key={`${tribe.name}-${index}`}
                    ref={el => {
                      slideRefs.current[index] = el
                    }}
                    className="tribe-org__carousel-slide"
                    aria-hidden={index !== safeIndex}
                  >
                    <TribeOrgContainer
                      tribesOrg={tribe}
                      isSnapshot={selectedSnapshot.value !== ''}
                      startDate={startDate}
                      endDate={endDate}
                      readOnly={readOnly}
                    />
                  </div>
                ))}
              </div>
            </div>
            </div>
          </div>
        ) : <Loader />}
    </div>
  )
}

export default TribeOrgPage
